import { Key, ReactText, useCallback, useEffect, useState } from 'react';
import { Cascader } from 'antd';
import { useParams } from 'react-router-dom';
import {
  Paging,
  Table,
  usePaging,
  useAsync,
  Form,
  Input,
  CustomFilter,
  EllipsisSpan,
  Modal,
} from '@maxtropy/components';
import { formatOptionData, getChildNodesByParentIds } from '@/shared/components/CascadingMultipleSelector/utils';
import { useQuery } from '../../../../utils/utils';
import styles from './index.module.scss';
import { getDeviceTypeTree } from '@/api/deviceType';
import { apiV2ComprehensiveLabelBindPost, apiV2DevicePagePost } from '@maxtropy/device-customer-apis-v2';

interface FilterParams {
  name?: string;
  tag?: string;
  typeId: Array<Array<number>>;
}

interface TableDataType {}

export interface DeviceModalRef {
  selectedRows?: any[];
}

const columns = () => {
  return [
    {
      title: '设备编号',
      dataIndex: 'code',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '设备名称',
      dataIndex: 'name',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '设备S/N码',
      dataIndex: 'sn',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '所属类目',
      dataIndex: 'typeName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '设备标签',
      dataIndex: 'tags',
      ellipsis: { showTitle: true },
      render: (v: string[]) => {
        const tags = v && v.join(', ');
        return <EllipsisSpan value={tags} />;
      },
    },
    {
      title: '所属组织',
      dataIndex: 'customerName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
  ];
};
interface Iprops {
  linkIds?: number[];
  labelId?: number;
  onChangeSelectKeys?: (keys?: number[]) => void;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const DeviceModal = ({ linkIds, labelId, onChangeSelectKeys, onConfirm, onCancel }: Iprops) => {
  console.log('linkIds :>> ', linkIds);
  console.log('labelId :>> ', labelId);
  const pagingInfo = usePaging();
  const [form] = Form.useForm();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [searchParams, setSearchParams] = useState<any>();
  const [selectedRows, setSelectedRows] = useState<any[]>();
  const [selectedItems, setSelectItems] = useState<any[]>((linkIds || []).map(id => ({ id })));

  //console.log('selectedItems :>> ', selectedItems);
  const { id } = useParams<{ id: string }>();

  const deviceTypeData = useAsync(getDeviceTypeTree);

  const { data, isLoading }: { data?: any[]; isLoading?: boolean } = useQuery(
    useCallback(async () => {
      const res = await apiV2DevicePagePost({
        page: pageOffset,
        size: pageSize,
        codeOrName: searchParams?.name,
        typeId: searchParams?.deviceTypes,
      });
      if (res) setTotalCount(res.total as number);
      return res.list;
    }, [id, pageOffset, pageSize, searchParams, setTotalCount])
  );

  const onReset = () => {
    form.resetFields();
    form.submit();
    setPageOffset(1);
  };

  const onFinish = (val: FilterParams) => {
    // const deviceTypes = getAllChild(val.typeId, formatOptionData(deviceTypeData));
    const deviceTypes = getChildNodesByParentIds(val.typeId, deviceTypeData?.tree);
    const params: any = {
      name: val.name,
      deviceTypes: deviceTypes,
    };
    setSearchParams(params);
    setPageOffset(1);
  };

  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys: (selectedRows || []).concat(selectedItems || []).map(item => item.id as Key),
    onChange: (rowKeys: any[], rows: any[]) => {
      const currentSelectedIds = new Set(rowKeys);

      // 从selectedItems中过滤出仍然被选中的项
      const remainingSelectedItems = selectedItems?.filter(item => currentSelectedIds.has(item.id)) || [];

      // 获取新增选中的项(在rows中但不在selectedItems中)
      const newSelectedRows = rows.filter(item => !!item && !selectedItems?.map(s => s.id).includes(item.id));

      setSelectItems(remainingSelectedItems);
      setSelectedRows(newSelectedRows);
    },
  };

  return (
    <Modal
      size="big"
      title="关联设备"
      open
      destroyOnClose
      onOk={() => {
        const selectedIds = (selectedRows || []).concat(selectedItems || []).map(item => item.id);
        apiV2ComprehensiveLabelBindPost({
          type: 3,
          labelId,
          ids: selectedIds,
        }).then(() => {
          onConfirm?.();
        });
      }}
      onCancel={() => {
        onCancel?.();
      }}
    >
      <div className={styles.deviceModal}>
        <CustomFilter<FilterParams> form={form} onFinish={val => onFinish(val as FilterParams)} onReset={onReset}>
          <Form.Item name="name" label="编号/名称">
            <Input placeholder={'请输入编号或名称查询'} style={{ width: 160 }} />
          </Form.Item>

          <Form.Item name="typeId" label="所属类目">
            <Cascader
              options={formatOptionData(deviceTypeData)}
              allowClear={false}
              fieldNames={{ children: 'child' }}
              multiple
              maxTagCount="responsive"
              showSearch={{
                filter: (inputValue, path) =>
                  path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1),
              }}
              placeholder={'请选择所属类目'}
            />
          </Form.Item>
        </CustomFilter>

        <Table<TableDataType>
          scroll={{ y: 300 }}
          rowKey="id"
          loading={isLoading}
          rowSelection={rowSelection}
          columns={columns()}
          dataSource={data}
          bottomLeftExtra={
            <div className={styles.tips}>
              <span>本次新增勾选</span>
              <span className={styles.red}>{selectedRows?.length || '0'}</span>
              <span>项，</span>
              <span>总计</span>
              <span className={styles.red}>{(selectedItems?.length || 0) + (selectedRows?.length || 0)}</span>
              <span>项</span>
            </div>
          }
        >
          <Paging pagingInfo={pagingInfo} />
        </Table>
      </div>
    </Modal>
  );
};

export default DeviceModal;
